import { useState } from 'react';
import { useLocation, Link } from 'wouter';

export function CarrierResetPassword() {
    const [, setLocation] = useLocation();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (newPassword.length < 10) {
            setError('Password must be at least 10 characters');
            return;
        }

        setLoading(true);

        try {
            const res = await fetch('/api/carrier/auth/reset-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword, confirmPassword }),
            });
            const data = await res.json();

            if (!res.ok) {
                setError(data.error || 'Reset failed');
                return;
            }

            setSuccess(true);
            setTimeout(() => setLocation('/carrier-login'), 3000);
        } catch (err: any) {
            setError(err.message || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full text-center shadow-xl">
                    <div className="text-4xl mb-4">⚠️</div>
                    <h1 className="text-xl font-bold text-slate-900 mb-2">Invalid Reset Link</h1>
                    <p className="text-slate-500 text-sm mb-4">This link is missing a reset token.</p>
                    <Link href="/carrier/forgot-password" className="font-medium text-primary hover:text-primary/80 text-sm">Request New Link</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md space-y-6">
                <div className="text-center space-y-2">
                    <span className="text-4xl filter drop-shadow-sm">🔐</span>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900">Reset Password</h2>
                    <p className="text-sm text-slate-500">Enter your new password below.</p>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-xl">
                    {success ? (
                        <div className="text-center">
                            <div className="p-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
                                Password reset successfully! Redirecting to login...
                            </div>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-slate-700">New Password</label>
                                    <input
                                        type="password"
                                        className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="At least 10 characters"
                                        required
                                        minLength={10}
                                        autoFocus
                                    />
                                    <p className="text-xs text-slate-400">
                                        Must be 10+ chars, with uppercase, lowercase, number, and special char.
                                    </p>
                                </div>

                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-slate-700">Confirm Password</label>
                                    <input
                                        type="password"
                                        className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Repeat your password"
                                        required
                                    />
                                    {confirmPassword && newPassword !== confirmPassword && (
                                        <p className="text-xs text-red-500">Passwords do not match</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="h-12 w-full bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold transition disabled:opacity-60"
                                    disabled={loading || (confirmPassword !== '' && newPassword !== confirmPassword)}
                                >
                                    {loading ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
